/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core';
// import { IInvoiceErrorState, IPayInvoiceResources, PayInvoiceComponent } from '@msdyn365-commerce-modules/invoice';
import { /*IInvoiceErrorState,*/ IPayInvoiceResources /*, PayInvoiceComponent*/ } from '@msdyn365-commerce-modules/invoice';
import React from 'react';

import { formatShortDate } from '../gdn-invoice-details';
// import { InvoiceDetailsModel } from '../helpers/invoice-data';
import { IGetGdnInvoiceDetailsResponse } from '../../../actions/DataServiceEntities.g';

export interface IInvoiceDetailsHeaderData {
    // invoiceDetails: InvoiceDetailsModel;
    gdnInvoiceDetails: IGetGdnInvoiceDetailsResponse;
}

export interface IInvoiceDetailsHeaderResources extends IPayInvoiceResources {
    custNumberLabel: string;
    salesOrderNumLabel: string;
    purchaseOrderNumLabel: string;
    shipToLabel: string;
    shipViaLabel: string;
    invoiceDateLabel: string;
    orderDateLabel: string;
    invoiceIdLabel: string;
}

export interface IInvoiceDetailsHeaderProps extends IComponentProps<IInvoiceDetailsHeaderData> {
    context: ICoreContext;
    className: string;
    resources: IInvoiceDetailsHeaderResources;
    // onError?(errorState: IInvoiceErrorState, reference?: React.RefObject<HTMLButtonElement>): void;
}

export interface IInvoiceDetailsHeaderComponent extends IComponent<IInvoiceDetailsHeaderProps> {}

const InvoiceDetailsHeader: React.FC<IInvoiceDetailsHeaderProps> = (props: IInvoiceDetailsHeaderProps): JSX.Element => {
    const {
        className,
        data: { /*invoiceDetails,*/ gdnInvoiceDetails },
        resources
        // onError
    } = props;

    const {
        custNumberLabel,
        salesOrderNumLabel,
        purchaseOrderNumLabel,
        shipToLabel,
        shipViaLabel,
        invoiceDateLabel,
        orderDateLabel
    } = resources;

    const invoiceNumberText = resources.invoiceIdLabel.replace('{0}', gdnInvoiceDetails.InvoiceDetails?.InvoiceNumber || '');
    const invoiceInfoContainerClassName = `${className}__invoice-info-container`;
    const orderInfoContainerClassName = `${invoiceInfoContainerClassName}__order-info-container`;
    const orderInfoClassName = `${orderInfoContainerClassName}__order-info`;
    const orderMainInfoClassName = `${orderInfoClassName}__main-info`;
    const actionsClassName = `${className}__actions`;
    const amountSummaryClassName = `${invoiceInfoContainerClassName}__amount-summary`;
    const invoiceDate = formatShortDate(props.context, gdnInvoiceDetails.InvoiceDetails?.InvoiceDate);
    const orderDate = formatShortDate(props.context, gdnInvoiceDetails.InvoiceDetails?.OrderDate);
    // const canPayInvoice = !!gdnInvoiceDetails.InvoiceDetails?.InvoiceAmountMst && gdnInvoiceDetails.InvoiceDetails?.InvoiceAmountMst > 0;
    // TODO: Look into why this is not used on invoice
    // const canPayInvoice = !!gdnInvoiceDetails.InvoiceDetails?.AmountDue && gdnInvoiceDetails.InvoiceDetails?.AmountDue > 0;

    return (
        <div className={className}>
            <div className={`${className}__invoice-id`}>{invoiceNumberText}</div>
            <div className={`${className}__order-info`}>
                <div className={`${className}__order-info--row`}>
                    <div className={`${className}__order-info--row-label`}>{custNumberLabel}</div>
                    <div className={`${className}__order-info--row-data`}>{`${gdnInvoiceDetails?.InvoiceDetails?.CustomerAccount}`}</div>
                </div>
                <div className={`${className}__order-info--row`}>
                    <div className={`${className}__order-info--row-label`}>{salesOrderNumLabel}</div>
                    <div className={`${className}__order-info--row-data`}>{`${gdnInvoiceDetails.InvoiceDetails?.SalesOrderNumber}`}</div>
                </div>
                <div className={`${className}__order-info--row`}>
                    <div className={`${className}__order-info--row-label`}>{purchaseOrderNumLabel}</div>
                    <div className={`${className}__order-info--row-data`}>{`${gdnInvoiceDetails.InvoiceDetails?.PoNumber}`}</div>
                </div>
                {gdnInvoiceDetails.InvoiceDetails?.DeliveryAddress && (
                    <div className={`${className}__order-info--row`}>
                        <div className={`${className}__order-info--row-label`}>{shipToLabel}</div>
                        <div className={`${className}__order-info--row-data-ship`}>
                            {`${gdnInvoiceDetails.InvoiceDetails?.DeliveryLocation}`}
                            <br />
                            <pre>{gdnInvoiceDetails.InvoiceDetails?.DeliveryAddress}</pre>
                        </div>
                    </div>
                )}
            </div>
            <div className={invoiceInfoContainerClassName}>
                <div className={orderInfoContainerClassName}>
                    <div className={orderInfoClassName}>
                        <div className={`${orderMainInfoClassName}`}>
                            <div className={`${orderMainInfoClassName}__order-placed-by`}>
                                <div className={`${orderMainInfoClassName}__order-placed-by--name`}>
                                    {gdnInvoiceDetails.InvoiceDetails?.InvoiceLocation}
                                </div>
                                <div className={`${orderMainInfoClassName}__order-placed-by--address`}>
                                    <pre>{gdnInvoiceDetails.InvoiceDetails?.InvoiceAddress}</pre>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={amountSummaryClassName}>
                    <div className={`${amountSummaryClassName}__amount-due-text`}>{`${invoiceDateLabel} ${invoiceDate}`}</div>
                    <div className={`${amountSummaryClassName}__amount-due-text`}>
                        {`${shipViaLabel} ${gdnInvoiceDetails.InvoiceDetails?.ShipVia}`}
                    </div>
                    <div className={`${amountSummaryClassName}__amount-due-text`}>{`${orderDateLabel} ${orderDate}`}</div>
                </div>
            </div>
            <div className={actionsClassName}>
                {/* <PayInvoiceComponent
                    context={props.context}
                    className={`${actionsClassName}__pay-invoice`}
                    resources={props.resources}
                    id={`${actionsClassName}__${invoiceDetails.id}`}
                    typeName={props.typeName}
                    data={{ invoice: invoiceDetails }}
                    disabled={!canPayInvoice}
                    onError={onError}
                /> */}
                <div className={`${actionsClassName}__print-button-container`}>
                    <i className={'order-confirmation__print'} onClick={() => window.print()} />
                </div>
            </div>
        </div>
    );
};

export const InvoiceDetailsHeaderComponent: React.FunctionComponent<IInvoiceDetailsHeaderProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IInvoiceDetailsHeaderComponent
>('InvoiceDetailsHeaderComponent', { component: InvoiceDetailsHeader });
